import { Component, Input, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { BaseChartDirective, NgChartsModule } from 'ng2-charts';
import { ChartConfiguration, ChartData, ChartEvent, ChartType } from 'chart.js';
import { CommonModule } from '@angular/common';

export interface ElementoGrafico {
  label: string;
  valor: number;
  percentual: number;
  cor: string;
}

@Component({
  selector: 'app-chart-pec',
  templateUrl: './chart-pec.component.html',
  styleUrls: ['./chart-pec.component.scss'],
  standalone: true,
  imports: [CommonModule, NgChartsModule]
})
export class ChartPecComponent implements OnInit {
  @ViewChild(BaseChartDirective) chart: BaseChartDirective | undefined;

  @Input() titulo: string = 'Gráfico';

  public chartContainerMinHeight = '300px';

  constructor(private cdr: ChangeDetectorRef) {}

  private _elementos: ElementoGrafico[] = [];
  private _totalProvidedExplicitly = false;
  private _total: number = 0;

  @Input()
  set total(value: number) {
    // Si se proporciona un total explícitamente (diferente de 0), marcarlo
    if (value !== 0) {
      this._totalProvidedExplicitly = true;
    }
    this._total = value;
  }
  get total(): number {
    return this._total;
  }

  @Input()
  set elementos(value: ElementoGrafico[]) {
    this._elementos = value;
    // Calcular el total automáticamente si no fue proporcionado explícitamente
    if (!this._totalProvidedExplicitly) {
      this._total = this._elementos.reduce((sum, e) => sum + e.valor, 0);
    }
    this.atualizarGrafico();
  }
  get elementos(): ElementoGrafico[] {
    return this._elementos;
  }

  public barChartType: ChartType = 'bar';
  public barChartData: ChartData<'bar'> = {
    labels: [],
    datasets: [
      {
        label: '',
        data: [],
        backgroundColor: [],
        borderColor: [],
        borderWidth: 1
      }
    ]
  };

  private chartInitialized = false;

  public barChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        bottom: 30
      }
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
        text: ''
      },
      tooltip: {
        callbacks: {
          title: () => {
            return this.titulo;
          },
          label: (context) => {
            if (context.dataIndex < this.elementos.length) {
              const elemento = this.elementos[context.dataIndex];
              const label = elemento.label;
              const percentage = context.parsed.y;
              const quantity = elemento.valor;
              const total = this.total;
              return [
                label,
                `${percentage}% (${quantity} registros)`
              ];
            }
            return '';
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          color: "rgba(0, 0, 0, 0.2)",
          lineWidth: 1
        },
        ticks: {
          color: "#000",
          maxRotation: 0,
          minRotation: 0,
          font: {
            size: 10,
            lineHeight: 1.3,
            weight: 'normal'
          },
          padding: 10,
          callback: (value: any) => {
            const label = this.elementos[value]?.label || '';
            const maxCharsPerLine = 12;

            if (label.length > maxCharsPerLine) {
              // Split text into multiple lines
              const words = label.split(' ');
              const lines: string[] = [];
              let currentLine = '';

              for (const word of words) {
                if ((currentLine + word).length > maxCharsPerLine && currentLine.length > 0) {
                  lines.push(currentLine);
                  currentLine = word;
                } else {
                  currentLine += (currentLine ? ' ' : '') + word;
                }
              }
              if (currentLine) lines.push(currentLine);
              return lines;
            }
            return label;
          }
        },
        afterFit: (scale: any) => {
          // Force a consistent height for x-axis to accommodate 2-line labels
          scale.height = 45;
        }
      },
      y: {
        display: true,
        title: {
          display: false,
          text: 'Porcentagem (%)'
        },
        beginAtZero: true,
        max: 100,
        grid: {
          color: "rgba(0, 0, 0, 0.2)",
          lineWidth: 1
        },
        ticks: {
          color: "#000",
          stepSize: 10,
          maxTicksLimit: 11
        }
      }
    }
  };

  ngOnInit(): void {
    // No hay inicialización, esperar a que los elementos sean asignados
  }

  private atualizarGrafico(): void {
    if (this.elementos.length === 0) {
      // Mostrar gráfico vacío
      this.barChartData = {
        labels: [],
        datasets: [
          {
            label: '',
            data: [],
            backgroundColor: [],
            borderColor: [],
            borderWidth: 1
          }
        ]
      };
      this.cdr.markForCheck();
      return;
    }

    // Crear nuevo objeto de datos para forzar detección de cambios
    const newData: ChartData<'bar'> = {
      labels: [...this.elementos.map(e => e.label)],
      datasets: [
        {
          label: '',
          data: [...this.elementos.map(e => e.percentual)],
          backgroundColor: [...this.elementos.map(e => e.cor)],
          borderColor: [...this.elementos.map(e => e.cor)],
          borderWidth: 1
        }
      ]
    };

    this.barChartData = newData;
    this.cdr.markForCheck();

    // Esperar a que Angular renderice el cambio
    setTimeout(() => {
      if (this.chart) {
        this.chart.update();
      }
    }, 100);
  }

}
